import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { IProduct } from 'src/app/models/products/product';
import { latestOutputAssets } from 'src/app/models/products/asset';
import { AuthService } from 'src/app/services/auth.service';
import { Permission } from 'src/app/constants/permission.constant';
import { ProductsService } from 'src/app/services/products.service';
import { MatDialog } from '@angular/material/dialog';
import { GenerateShopifyAssetsSyncComponent } from 'src/app/components/home/products/actions/generate-shopify-assets-sync/generate-shopify-assets-sync.component';
import {
  parseStepContentData,
  regroupOutputAssetsByFormula,
  setAllSelected,
  selectAllInTouch,
  onCardSelected,
  checkAllSelected,
  countSelectedAssets,
  isDownloadDisabled,
  hasSelectableAssets,
  ParseAssetContext,
} from '../product-asset.utils';

@Component({
  selector: 'app-output-assets-ev-view',
  templateUrl: './output-assets-ev-view.component.html',
  styleUrls: ['./output-assets-ev-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class OutputAssetsEvViewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() product!: IProduct;
  @Input() colPerScreen = 3;
  @Input() editCallback!: (data: any) => void;
  @Input() activated = false;
  @ViewChild('cardItem') cardItemEl!: ElementRef;

  @Output() bucketLoaded = new EventEmitter<{ count: number }>();

  Permission = Permission;
  content: any[] = [];
  isLoading = false;
  allSelected = false;
  selectedCount = 0;
  displaySelectTouch: { [key: string]: boolean } = {};
  rowHeight = 'fit';

  private destroy$ = new Subject<void>();
  private hasFetched = false;

  constructor(
    public authService: AuthService,
    private productsService: ProductsService,
    public dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    if (this.activated) this.fetchBucket();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activated'] && this.activated && !this.hasFetched) {
      this.fetchBucket();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fetchBucket(): void {
    if (!this.product?.id) return;
    this.hasFetched = true;
    this.isLoading = true;
    this.productsService
      .getListOutputAssets(this.product.id, 'ev')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value: latestOutputAssets[]) => {
          const groups = regroupOutputAssetsByFormula(value).map((group) => ({
            ...group,
            selectedTouch: false,
            quantitySelected: 0,
          }));
          this.content = parseStepContentData(groups, this.parseContext, true);
          this.content.forEach((format: any) => {
            this.displaySelectTouch[format.id] = hasSelectableAssets(this.content, format.id);
          });
          this.allSelected = false;
          this.selectedCount = 0;
          this.isLoading = false;
          this.bucketLoaded.emit({ count: this.content.length });
        },
      });
  }

  private get parseContext(): ParseAssetContext {
    return {
      step: 'outputAssets',
      listProduction: [],
      hasPreReleasePermission: this.authService.hasPermission(Permission.PostAssetsPreRelease),
    };
  }

  setAll(completed: boolean): void {
    const result = setAllSelected(this.content, completed);
    this.allSelected = completed;
    this.selectedCount = result.count;
  }

  onTouchAllSelected(completed: boolean, item: any): void {
    selectAllInTouch(this.content, item.id, completed);
    this.allSelected = checkAllSelected(this.content);
    this.selectedCount = countSelectedAssets(this.content);
  }

  onCardSelected(payload: { touchId: string; data: any }): void {
    onCardSelected(this.content, payload.touchId);
    this.allSelected = checkAllSelected(this.content);
    this.selectedCount = countSelectedAssets(this.content);
  }

  onCardDone(): void {
    setTimeout(() => {
      if (!this.cardItemEl) return;
      this.rowHeight = `${this.cardItemEl.nativeElement.firstChild.offsetWidth}:${this.cardItemEl.nativeElement.firstChild.offsetHeight}`;
      this.isLoading = false;
    });
  }

  getSelectedPostAssetIds(): string[] {
    const postAssetIds = this.content.flatMap((format: any) =>
      format.assets
        .filter((asset: any) => asset.selected && asset.postAssetId)
        .map((asset: any) => asset.postAssetId),
    );
    return [...new Set(postAssetIds)];
  }

  disableButtonDownload(): boolean {
    return isDownloadDisabled(this.content);
  }

  downloadMultiple(): void {
    let count = 0;
    this.content.forEach((t: any) => {
      t.assets.forEach((asset: any) => {
        if (asset.selected) {
          setTimeout(
            () => {
              const tmp = asset.rawUri.split('?')[0].split('/');
              const fileName = tmp[tmp.length - 1].split('.');
              const download = `${fileName[0]}.${fileName[1]}`;
              this.productsService
                .downloadSingleImage(asset.rawUri.split('//')[1])
                .subscribe((res) => {
                  const blob: Blob = res.body as Blob;
                  const a = document.createElement('a');
                  const fileData = window.URL.createObjectURL(blob);
                  a.href = fileData;
                  a.download = download;
                  a.click();
                  window.URL.revokeObjectURL(fileData);
                });
            },
            (count += 3000),
          );
        }
      });
    });
  }

  downloadProductLabelData(): void {
    this.productsService.downloadProductLabelData(this.product.id!).subscribe((blob) => {
      const a = document.createElement('a');
      const url = window.URL.createObjectURL(blob);
      a.href = url;
      a.download = `${this.product.barcode}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  }

  syncProductAssets(): void {
    this.dialog.open(GenerateShopifyAssetsSyncComponent, {
      data: {
        organizationId: this.product.organizationId,
        batchId: this.product.batchId,
        isBatch: false,
        productIds: [this.product.id],
      },
    });
  }

  formatTitle(group: any): string {
    return group.name;
  }

  formatSubtitle(group: any): string {
    const assetCount = (group.assets ?? []).length;
    const assetLabel = assetCount === 1 ? 'asset' : 'assets';
    return `EV · ${assetCount} ${assetLabel}`;
  }

}
